/** Capture permission for THIS origin, collapsed to the only distinction the mDNS question
 * needs. All three engines mask local host candidates behind a random `.local` name unless the
 * origin holds camera or microphone access, so `prompt` and `denied` are one bucket: not granted.
 * Every failure mode is `unknown`, which the UI renders as nothing rather than guessing.
 *
 * RFC 8828 is the neighbouring document, not the source: it requires consent before Mode 1 and
 * notes that tying that consent to getUserMedia is "one potential mechanism". The masking itself
 * was specified in draft-ietf-mmusic-mdns-ice-candidates, which never became an RFC. So this is
 * convergent engine behaviour, not a standard, and the comments must not claim otherwise. */
export type CapturePermission = "granted" | "not-granted" | "unknown";

const CAPTURE_NAMES = ["camera", "microphone"] as const;

/** Reads permission state only — never enumerateDevices(), which can raise an OS-level capture
 * prompt on macOS Firefox. A leak test must not make the operating system ask the user for
 * anything, so this file has no path that touches a device. */
export function readCapturePermission(timeoutMs = 1000): Promise<CapturePermission> {
  // `navigator.permissions` is undefined during prerender AND in insecure contexts, and this
  // tool has no InsecureContextError guard of its own, so both checks are load-bearing.
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return Promise.resolve("unknown");
  }

  // allSettled, not all: one query that rejects (or a name the browser refuses) must not sink
  // the other's already-resolved answer. The fingerprint collector documents this exact bug.
  const reads = Promise.allSettled(
    CAPTURE_NAMES.map((name) => navigator.permissions.query({ name: name as PermissionName }))
  ).then((results): CapturePermission => {
    let answered = false;
    for (const result of results) {
      if (result.status !== "fulfilled") continue;
      answered = true;
      if (result.value.state === "granted") return "granted";
    }
    // Every current engine answers these two names (Chrome 64, Safari 16, Firefox 132), so the
    // refusal path is now only reached by browsers older than that. Kept anyway, because
    // "nothing answered" is "we could not look", which is not the same claim as "not granted".
    return answered ? "not-granted" : "unknown";
  });

  // A permissions query that never settles is real in some WebViews. Own deadline so it can
  // never hold anything up.
  return Promise.race([
    reads,
    new Promise<CapturePermission>((resolve) => {
      setTimeout(() => resolve("unknown"), timeoutMs);
    }),
  ]).catch(() => "unknown");
}
